// src/App.jsx
import "./i18n";
import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";

import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { AuthProvider } from "./context/AuthContext";
import { ProfileProvider } from "./context/ProfileContext";
import { PrivateRoute } from "./components/PrivateRoute";
import ProtectedRoute from "./components/ProtectedRoute";
import SoftGate from "./components/SoftGate";
import RequireStaff from "./components/RequireStaff";
import { Toaster } from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PresentationMode from "./components/PresentationMode";

import { publicRoutes, privateRoutes } from "./routes";
import Loading from "./components/Loading";
import NotFound from "./components/NotFound";

// Pages qui portent leur PROPRE header/footer (landings, dashboards autonomes,
// login plein écran) : on masque le chrome global pour éviter le doublon d'en-tête.
function useOwnChrome() {
  const { pathname } = useLocation();
  const p = (pathname || "/").replace(/\/+$/, "") || "/";
  return (
    p === "/" ||
    p === "/login" ||
    p === "/notaires" ||
    p === "/cercle-experts" ||
    p.startsWith("/proposition/") ||
    p === "/transmission-professionnelle" ||
    p === "/invitation-notaire" ||
    p === "/bienvenue" ||
    p === "/onboarding" ||
    p.startsWith("/onboarding/")
  );
}
function GlobalHeader() { return useOwnChrome() ? null : <Header />; }
function GlobalFooter() { return useOwnChrome() ? null : <Footer />; }

if (process.env.NODE_ENV === "production") {
  // Marqueur de version déployée : passe REACT_APP_BUILD au build (ex.
  // `REACT_APP_BUILD=$(date +%Y-%m-%d-%H%M) npm run build`) pour vérifier en
  // console (window.__ARTKIV_BUILD__) quelle version est réellement en ligne.
  window.__ARTKIV_BUILD__ = process.env.REACT_APP_BUILD || "2026-08-01";
}

/* =========================================================
   ROUTE GROUPS
========================================================= */

const staffRoutes = [
  "/admin/users",
  "/admin/dashboard",
  "/admin/ref-pricing",
  "/admin",
  "/admin/subscriptions/events",
  "/admin/grant-free",
  "/team/actualites",
  "/team/relances",
  "/team/demos",
  "/team/partenaires",
  "/team/galeries",
];

const expertRoutes = [
  "/expert/dashboard",
  "/expert/workspace",
  "/expert/import-artprice",
  "/expert/missions",
];

// ✅ Ajout valuation ici + couvre /partner
const partnerRoutes = ["/partner", "/partner/transport", "/partner/insurance", "/partner/valuation"];

const isStaffRoute = (path) => staffRoutes.some((r) => path.startsWith(r));
const isExpertRoute = (path) => expertRoutes.some((r) => path.startsWith(r));
const isPartnerRoute = (path) => partnerRoutes.some((r) => path.startsWith(r));

const partnerTypeForPath = (path) => {
  if (path.startsWith("/partner/transport")) return "transport";
  if (path.startsWith("/partner/insurance")) return "insurance";
  if (path.startsWith("/partner/valuation")) return "valuation";
  return null; // ex: "/partner" ou "/partner/profile"
};

/* =========================================================
   SOFT GATES (ROLE × PLAN)
   - estimation = VIEW (soft)
   - succession = VIEW (soft)
   - AI-RUN actions = RUN (soft)
========================================================= */

// ⚠️ Vérifié le 31/08 : AUCUNE des 4 méthodes d'estimation (points, comparables,
// assurance, succession), ni le hub /estimation, ni le flux de validation experte,
// ni ConservationReportForm (/analyse-images = analyse_conservation.py, pure
// heuristique) n'appellent le moindre endpoint OpenAI/vision-api. Ces routes
// étaient gatées "IA" par erreur — retirées (même logique que le fix /succession :
// ne pas bloquer une page qui n'utilise pas d'IA).
const isAiRunRoute = (path) =>
  // ✅ Import OCR par lot / depuis un document = déclenche des appels OpenAI facturés.
  path === "/import-batch" ||
  path === "/create-from-document";

const isAiViewRoute = (path) =>
  // ✅ Suivi IA / conservation “lecture” — seules routes confirmées IA (suivi_ia_api.py)
  path.startsWith("/suivi-ia/") ||
  (path.startsWith("/conservation/") && path.endsWith("/suivi")) ||
  // ✅ Market
  path === "/services/market-valuations" ||
  path === "/art-market-insights";
// standard/premium (préparation de la transmission = libre, matching IA = premium,
// cf. le switch "standard"/"premium" dans SuccessionPlanningPage.tsx). La gater au
// niveau de la route bloquait toute la page, pas seulement le matching.

/* =========================================================
   ROUTING
========================================================= */

function AppRoutes() {

  // ✅ CORRECTION : on ne force plus de key artificielle sur chaque route.
  const withFreshKey = (el) => el;

  const isAlreadyProtected = (el) =>
    React.isValidElement(el) && el.type === ProtectedRoute;

  return (
    <Routes>
      {/* ================= PUBLIC ================= */}
      {publicRoutes.map((route, index) => (
        <Route
          key={`public-${index}`}
          path={route.path}
          element={withFreshKey(route.element)}
        />
      ))}

      {/* Presentation mode special */}
      <Route
        path="/presentation/collection/:collection_id"
        element={
          <PrivateRoute>
            <PresentationMode />
          </PrivateRoute>
        }
      />

      {/* ================= PRIVATE ================= */}
      {privateRoutes.map((route, index) => {
        if (route.path === "/presentation/collection/:collection_id") return null;

        const freshElement = withFreshKey(route.element);

        /* ---------- PARTNER (strict role + approved + partner_type) ---------- */
        if (isPartnerRoute(route.path)) {
          const pType = partnerTypeForPath(route.path);

          return (
            <Route
              key={`private-${index}`}
              path={route.path}
              element={
                <PrivateRoute>
                  <ProtectedRoute
                    allowedRoles={["partner", "expert", "admin"]}
                    requireApproved
                    requirePartnerType={pType || undefined}
                    redirectTo="/dashboard"
                  >
                    {freshElement}
                  </ProtectedRoute>
                </PrivateRoute>
              }
            />
          );
        }

        /* ---------- STAFF ---------- */
        if (isStaffRoute(route.path)) {
          return (
            <Route
              key={`private-${index}`}
              path={route.path}
              element={
                <PrivateRoute>
                  <RequireStaff>{freshElement}</RequireStaff>
                </PrivateRoute>
              }
            />
          );
        }

        /* ---------- EXPERT ---------- */
        if (isExpertRoute(route.path)) {
          return (
            <Route
              key={`private-${index}`}
              path={route.path}
              element={
                <PrivateRoute>
                  <RequireStaff requireExpert>{freshElement}</RequireStaff>
                </PrivateRoute>
              }
            />
          );
        }

        /* ---------- ONBOARDING (auth seule, aucun gating de plan) ---------- */
        if (route.path === "/onboarding" || route.path.startsWith("/onboarding/")) {
          return (
            <Route
              key={`private-${index}`}
              path={route.path}
              element={<PrivateRoute>{freshElement}</PrivateRoute>}
            />
          );
        }

        /* ---------- DASHBOARD ---------- */
        if (route.path === "/dashboard" || route.path === "/mes-missions") {
          return (
            <Route
              key={`private-${index}`}
              path={route.path}
              element={<PrivateRoute>{freshElement}</PrivateRoute>}
            />
          );
        }

        /* ---------- ARTWORK ROUTES (auth only) ---------- */
        if (route.path.includes("/artwork/")) {
          return (
            <Route
              key={`private-${index}`}
              path={route.path}
              element={<PrivateRoute>{freshElement}</PrivateRoute>}
            />
          );
        }

        /* ---------- SOFT GATES ---------- */
        let gatedElement = freshElement;

        // Soft-gate IA RUN routes (actions)
        if (isAiRunRoute(route.path)) {
          gatedElement = (
            <SoftGate
              rule="ai_run"
              title="Activer les analyses IA"
              subtitle="Tu peux explorer, mais lancer des analyses nécessite Premium (ou Advisor Premium / Family Office)."
            >
              {freshElement}
            </SoftGate>
          );
        } else if (isAiViewRoute(route.path)) {
          // Soft-gate IA VIEW routes (lecture)
          gatedElement = (
            <SoftGate
              rule="ai_view"
              title="Accéder à ce module avancé"
              subtitle="Tu peux explorer l’interface, mais l’activation complète nécessite Premium (ou un plan Pro)."
            >
              {freshElement}
            </SoftGate>
          );
        }

        /* ---------- DEFAULT PRIVATE ---------- */
        const wrappedWithPlan = isAlreadyProtected(gatedElement) ? (
          gatedElement
        ) : (
          <ProtectedRoute>{gatedElement}</ProtectedRoute>
        );

        return (
          <Route
            key={`private-${index}`}
            path={route.path}
            element={<PrivateRoute>{wrappedWithPlan}</PrivateRoute>}
          />
        );
      })}

      {/* 404 réel : afficher NotFound au lieu de rediriger silencieusement vers "/" */}
      <Route path="*" element={<NotFound backTo="/" />} />
    </Routes>
  );
}

/* =========================================================
   APP ROOT
========================================================= */

export default function App() {
  useEffect(() => {
    const onRejection = (e) => {
      console.error("❌ UnhandledRejection:", e.reason);
      if (e?.reason?.stack) console.error(e.reason.stack);
    };
    const onError = (e) => {
      console.error("❌ WindowError:", e.error || e.message);
      if (e?.error?.stack) console.error(e.error.stack);
    };

    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onError);
    };
  }, []);

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      {/* 29 fichiers (dont ExpertDashboard : sauvegarde de profil, upload de
          CV) importent `toast` depuis react-toastify plutôt que
          react-hot-toast, mais son conteneur n'était jamais monté — leurs
          appels toast.success/toast.error ne s'affichaient jamais nulle
          part, sans erreur console. D'où l'impression que "Enregistrer" ou
          "Télécharger" ne faisait rien. Retour terrain 11/09/2026. */}
      <ToastContainer position="top-center" autoClose={3000} />
      <AuthProvider>
        <ProfileProvider>
          <Router>
            <div className="min-h-screen flex flex-col bg-white text-neutral-900">
              <GlobalHeader />
              <main className="flex-1">
                <Suspense fallback={<Loading />}>
                  <AppRoutes />
                </Suspense>
              </main>
              <GlobalFooter />
            </div>
          </Router>
        </ProfileProvider>
      </AuthProvider>
    </>
  );
}